import { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../../core/auth/authSlice";
import AcedTextBox from "../../../../../scenes/messages/chat/AcedTextBox";
import { PostApiRoutes } from "../../../../../shared/api-routes/post/post.api-routes";
import { User } from "../../../../../shared/models";
import http from "../../../../../shared/utils/http";
import ClickIconMenu from "../../../../essentials/icon-buttons/click-icon/ClickIconMenu";
import ToggleIcon from "../../../../essentials/icon-buttons/toggle-icon/ToggleIcon";
import AcedModal from "../../../../essentials/modals/aced-modal/AcedModal";
import ConfirmationModal from "../../../../essentials/modals/confirmation-modal/ConfirmationModal";
import UserCard, {
	UserPreviewInfo,
} from "../../../../essentials/user-card/UserCard";
import LikeModal from "../../like-modal/LikeModal";
import CommentReplyList from "../comment-reply-list/CommentReplyList";
import "./Comment.scss";
import CommentReply from "./CommentReply";

export interface CommentInfo {
	id: string;
	author: UserPreviewInfo;
	content: string;
	isLiked: boolean;
	likesCount: number;
	repliesCount: number;
}

export interface CommentProps {
	comment: CommentInfo;
	onDeleteComment: any;
	postId: string;
}

const Comment: React.FC<CommentProps> = ({
	comment,
	onDeleteComment,
	postId,
}) => {
	const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>();
	const [commentRepliesCount, setCommentRepliesCount] = useState<number>(
		comment.repliesCount
	);
	const [isLiked, setIsLiked] = useState<boolean>(comment.isLiked);
	const [likesCount, setLikesCount] = useState<number>(comment.likesCount);

	const [areRepliesOpen, setRepliesOpen] = useState<boolean>(false);

	const { actorId } = useSelector(selectUser) as User;

	const deleteComment = () => {
		http
			.delete(PostApiRoutes.DELETE_COMMENT(postId, comment.id))
			.then((res) => {
				setDeleteModalOpen(false);
				onDeleteComment(comment.id);
			});
	};

	const likeUnlike = () => {
		if (isLiked) {
			http.delete(PostApiRoutes.UNLIKE_COMMENT(postId, comment.id));
			setIsLiked(false);
			setLikesCount((prev) => prev - 1);
		} else if (!isLiked) {
			http.post(PostApiRoutes.LIKE_COMMENT(postId, comment.id));
			setIsLiked(true);
			setLikesCount((prev) => prev + 1);
		}
	};

	const openCloseReplies = () => {
		if (areRepliesOpen) {
			setRepliesOpen(false);
		} else if (!areRepliesOpen) {
			setRepliesOpen(true);
		}
	};

	const [isLikeModalOpen, setLikeModalOpen] = useState<boolean>(false);

	const [isReplyCommentOpen, setReplyCommentOpen] = useState<boolean>(false);
	const [writeReply, setWriteReply] = useState<string>("");

	const [newReplies, setNewReplies] = useState<CommentInfo[]>([]);

	const onAddReply = () => {
		http
			.post(PostApiRoutes.REPLY_TO_COMMENT(postId, comment.id), {
				content: writeReply,
			})
			.then((res: CommentInfo) => {
				setWriteReply("");
				setReplyCommentOpen(false);
				setNewReplies((prev) => [...prev, res]);
			});
	};

	const onClickReply = () => {
		setReplyCommentOpen(true);
	};

	const menuOptions = () => {
		const normalOptions = [{ text: "Copy Link" }];
		return actorId === comment.author.id
			? [
					{ text: "Edit" },
					{
						text: "Delete",
						color: "red",
						action: () => {
							setDeleteModalOpen(true);
						},
					},
					...normalOptions,
			  ]
			: normalOptions;
	};

	return (
		<div className="comment">
			<div className="comment-content">
				<div>
					<UserCard
						userInfo={comment.author}
						size="s"
					/>
					<p className="comment-text">{comment.content}</p>
				</div>

				<div className="actions">
					<ClickIconMenu
						icon="dots"
						size="s"
						options={menuOptions()}
					/>
					{/* <ClickIcon icon="edit" size="s" tooltip="Edit Comment (WIP)" />
            <DeleteButton
              onClick={() => {
                setDeleteModalOpen(true);
              }}
              tooltip="Delete Comment"
              size="s"
            />*/}
					<ConfirmationModal
						title="Delete comment?"
						state={{
							isModalOpen: isDeleteModalOpen,
							setModalOpen: setDeleteModalOpen,
						}}
						onConfirm={deleteComment}
					/>
				</div>
			</div>
			<AcedModal
				state={{ isModalOpen: isLikeModalOpen, setModalOpen: setLikeModalOpen }}
				title="Likes"
			>
				<LikeModal
					post_id={postId}
					comment_id={comment.id}
				/>
			</AcedModal>
			<div className="comment-footer">
				<div className="comment-footer-actions">
					<div className="element">
						<ToggleIcon
							icon="like"
							onClick={likeUnlike}
							isToggled={isLiked}
							size={16}
							tooltip="Like Comment"
						/>
						<p
							className="activex"
							onClick={() => {
								setLikeModalOpen(true);
							}}
						>
							{likesCount}
						</p>
					</div>

					<p
						className="activex"
						onClick={onClickReply}
					>
						Reply
					</p>
				</div>
				{areRepliesOpen && (
					<CommentReplyList
						onClickReply={onClickReply}
						onDeleteComment={onDeleteComment}
						parentComment_id={comment.id}
						postId={postId}
						newRepliesId={newReplies?.map((reply) => {
							return reply.id;
						})}
					/>
				)}
				{commentRepliesCount != 0 && (
					<p
						className="load-replies activex"
						onClick={openCloseReplies}
					>
						{areRepliesOpen ? "- Hide Replies" : "+ Load Replies"}
					</p>
				)}
				{newReplies?.map((newReply: CommentInfo) => (
					<CommentReply
						key={newReply.id}
						postId={postId}
						comment={newReply}
						onDeleteReply={() => {
							setNewReplies((prev) =>
								prev.filter((newReply) => newReply.id !== newReply.id)
							);
						}}
						onClickReply={onClickReply}
					/>
				))}
				{isReplyCommentOpen && (
					<AcedTextBox
						placeholder="Reply..."
						text={writeReply}
						setText={setWriteReply}
						onSend={onAddReply}
						mode="dark"
					/>
				)}
			</div>
		</div>
	);
};

export default Comment;
